import { useState, useMemo } from "react";
import { Maximize2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import CtaSection from "@/components/CtaSection";
import { PROJECTS } from "@/data/gallery";
import { SERVICE_FILTERS } from "@/data/site";

function Thumb({ project, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(project)}
      className="group relative block w-full text-left bg-ink overflow-hidden border border-gold/20"
      style={{ aspectRatio: project.aspect || "3 / 4" }}
      aria-label={`View before and after: ${project.title}`}
      data-testid={`work-item-${project.id}`}
    >
      <picture>
        <source srcSet={`${project.after}.webp`} type="image/webp" />
        <img
          src={`${project.after}.jpg`}
          alt={`After — ${project.title}, ${project.label} in Greensboro NC`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </picture>
      <span className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" aria-hidden="true" />
      <span className="absolute top-3 left-3 eyebrow !tracking-[0.14em] bg-white/90 text-ink px-2.5 py-1">
        {project.label}
      </span>
      <span className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
        <span className="font-serif text-xl text-white">{project.title}</span>
        <span className="shrink-0 h-9 w-9 rounded-full bg-white/90 text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
          <Maximize2 size={16} />
        </span>
      </span>
    </button>
  );
}

export default function OurWork() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const visible = useMemo(
    () =>
      filter === "All"
        ? PROJECTS
        : PROJECTS.filter((p) => p.tags.includes(filter)),
    [filter]
  );

  return (
    <>
      <Seo
        title="Before & After Property Services | Greensboro NC"
        description="See before and after results from cleaning, move-in/move-out, and property projects by Authentically Yours in Greensboro, NC."
        path="/our-work"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Our Work", path: "/our-work" },
        ]}
      />
      <PageHero
        eyebrow="Our Work"
        title="The Difference Is in the Details"
        intro="Real before-and-after results from spaces we've helped transform across the Greensboro area. Tap any project to compare."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Our Work", path: "/our-work" },
        ]}
      />

      <div className="container-x py-16 sm:py-20">
        {/* Filters */}
        <div className="flex flex-wrap gap-2.5" role="group" aria-label="Filter projects">
          {SERVICE_FILTERS.map((f) => {
            const on = filter === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                aria-pressed={on}
                className={`px-4 py-2 text-sm font-medium tracking-wide border transition-colors ${
                  on
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-ink/70 border-ink/20 hover:border-gold"
                }`}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {f}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {visible.length > 0 ? (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Thumb project={p} onOpen={setActive} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 border border-dashed border-gold/40 bg-blush-soft/40 py-20 text-center" data-testid="work-empty">
            <p className="font-serif text-3xl text-ink">New projects coming soon</p>
            <p className="mt-3 text-ink/60 max-w-md mx-auto">
              We're adding more {filter.toLowerCase()} before-and-after projects. Check back shortly.
            </p>
          </div>
        )}
      </div>

      {active && <Lightbox project={active} onClose={() => setActive(null)} />}

      <CtaSection />
    </>
  );
}
